import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { 
  CheckCircle, XCircle, Loader2, Play, Database, Tag
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const FEATURE_COLORS = {
  branding: 'bg-purple-100 text-purple-700',
  waivers: 'bg-blue-100 text-blue-700',
  'check-in': 'bg-green-100 text-green-700',
  customers: 'bg-orange-100 text-orange-700',
  reviews: 'bg-yellow-100 text-yellow-700',
  relations: 'bg-pink-100 text-pink-700',
  database: 'bg-gray-100 text-gray-700'
};

const PostgresAssuranceTestsSection = () => {
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);

  const runTests = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/health/tests/postgres-assurance-flow`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });
      
      const data = await response.json();
      setResults(data);
      
      if (data.failed === 0) {
        toast.success(`PostgreSQL Assurance: All ${data.total} tests passed!`);
      } else {
        toast.warning(`PostgreSQL Assurance: ${data.passed}/${data.total} tests passed`);
      }
    } catch (error) {
      toast.error('Failed to run PostgreSQL assurance tests');
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (passed) => {
    if (passed) return <CheckCircle className="w-5 h-5 text-green-500" />;
    return <XCircle className="w-5 h-5 text-red-500" />;
  };

  const getFeatureBadge = (feature) => {
    const colorClass = FEATURE_COLORS[feature] || 'bg-gray-100 text-gray-700';
    return (
      <span className={`px-2 py-0.5 text-xs font-medium rounded ${colorClass}`}>
        {feature}
      </span>
    );
  };

  return (
    <Card className="border-indigo-200 bg-indigo-50/30">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Database className="w-5 h-5 text-indigo-600" />
            <CardTitle className="text-lg">PostgreSQL Assurance Tests</CardTitle>
          </div>
          <Button 
            onClick={runTests} 
            disabled={loading}
            size="sm"
            className="bg-indigo-600 hover:bg-indigo-700"
          >
            {loading ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Running...</>
            ) : (
              <><Play className="w-4 h-4 mr-2" /> Run Tests</>
            )}
          </Button>
        </div>
        <p className="text-sm text-gray-600 mt-1">
          Verifies ALL application features use PostgreSQL: Branding, Waivers, Reviews, Check-in, Customers, Relations
        </p>
      </CardHeader>
      
      <CardContent>
        {!results ? (
          <div className="text-center py-8 text-gray-500">
            <Database className="w-12 h-12 mx-auto mb-3 text-indigo-300" />
            <p>Click &quot;Run Tests&quot; to verify PostgreSQL migration</p>
            <div className="flex flex-wrap justify-center gap-2 mt-4">
              {Object.entries(FEATURE_COLORS).map(([feature, _]) => (
                <span key={feature} className={`px-2 py-1 text-xs rounded ${FEATURE_COLORS[feature]}`}>
                  {feature}
                </span>
              ))}
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Summary */}
            <div className="flex items-center justify-between p-3 bg-white rounded-lg border">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                  results.failed === 0 ? 'bg-green-100' : 'bg-red-100'
                }`}>
                  {results.failed === 0 ? (
                    <CheckCircle className="w-6 h-6 text-green-600" />
                  ) : (
                    <XCircle className="w-6 h-6 text-red-600" />
                  )}
                </div>
                <div>
                  <p className="font-medium">{results.suite}</p>
                  <p className="text-sm text-gray-500">
                    {results.passed}/{results.total} passed ({results.pass_rate})
                  </p>
                </div>
              </div>
              <div className="flex gap-2">
                <span className="px-2 py-1 bg-green-100 text-green-700 text-xs font-medium rounded">
                  {results.passed} Passed
                </span>
                {results.failed > 0 && (
                  <span className="px-2 py-1 bg-red-100 text-red-700 text-xs font-medium rounded">
                    {results.failed} Failed
                  </span>
                )}
              </div>
            </div>

            {/* Feature Summary */}
            {results.feature_summary && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                {Object.entries(results.feature_summary).map(([feature, counts]) => (
                  <div 
                    key={feature}
                    className={`p-2 rounded border ${
                      counts.failed === 0 ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'
                    }`}
                  >
                    <div className="flex items-center gap-1 mb-1">
                      <Tag className="w-3 h-3" />
                      <span className="text-xs font-medium capitalize">{feature}</span>
                    </div>
                    <div className="text-xs text-gray-600">
                      {counts.passed}/{counts.passed + counts.failed} passed
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Test Results */}
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {results.tests?.map((test, idx) => (
                <div 
                  key={idx}
                  className={`flex items-center justify-between p-2 rounded border ${
                    test.passed ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'
                  }`}
                >
                  <div className="flex items-center gap-2 flex-1">
                    {getStatusIcon(test.passed)}
                    <span className="font-medium text-sm">{test.test}</span>
                    {getFeatureBadge(test.feature)}
                    {test.uses_postgres && (
                      <Database className="w-3 h-3 text-indigo-500" title="PostgreSQL" />
                    )}
                  </div>
                  <span className="text-xs text-gray-500 max-w-xs truncate" title={test.details}>
                    {test.details}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default PostgresAssuranceTestsSection;
